/**
 * Autocomplete suggestions list (Funnelback JSON++).
 */
import { RefObject, useEffect, useRef, useState } from 'react';
import Suggestion, { FunnelbackSuggestion } from '../suggestion';

type Props = {
  suggestions: FunnelbackSuggestion[];
  focusedIndex: number | null;
  onSuggestionClick: (suggestion: FunnelbackSuggestion) => void;
  onSuggestionKeyDown?: (
    e: React.KeyboardEvent<HTMLLIElement>,
    index: number,
  ) => void;
  onSuggestionFocus?: (index: number) => void;
  listRef?: RefObject<HTMLUListElement | null>;
  baseClassName: string;
};

const Suggestions = (props: Props) => {
  const {
    suggestions,
    focusedIndex,
    onSuggestionClick,
    onSuggestionKeyDown,
    onSuggestionFocus,
    listRef,
    baseClassName,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const hasVerticalOverflow =
          containerRef.current.scrollHeight > containerRef.current.clientHeight;
        setHasOverflow(hasVerticalOverflow);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [suggestions]);

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={`${baseClassName}__suggestions`}
      role="listbox"
      id={`${baseClassName}-suggestions`}
    >
      <div
        ref={containerRef}
        className={`${baseClassName}__viewport ${baseClassName}__viewport${hasOverflow ? ` ${baseClassName}__viewport--has-overflow` : ''}`}
      >
        <ul ref={listRef} className={`${baseClassName}__list`}>
          {suggestions.map((suggestion, index) => (
            <Suggestion
              key={index}
              suggestion={suggestion}
              index={index}
              isFocused={focusedIndex === index}
              baseClassName={baseClassName}
              onClick={onSuggestionClick}
              onKeyDown={onSuggestionKeyDown}
              onFocus={onSuggestionFocus}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Suggestions;
export type { FunnelbackSuggestion };
